import React, { useState, useEffect, useRef } from "react";
import { useUiContextStore } from "../../stores/uiContextStore";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  Loader,
  Plus,
  Edit2,
  Trash2,
  Server,
  Cloud,
  Calendar,
  Clock,
  Layers,
  X,
  ChevronRight,
  MoreVertical,
  Globe,
  Plug,
  Shield,
  Box,
  Activity,
} from "lucide-react";
import {
  getServiceById,
  updateService,
  deleteService,
  addServiceComponent,
  removeServiceComponent,
  updateServiceComponentMapping,
  linkAssetToService,
  removeServiceAsset,
  createAssetDeployment,
  updateAssetDeployment,
  ServiceResponse,
  ServiceComponentMapping,
  ServiceComponentDeployment,
  ServiceStatus,
  listOrgUnits,
  OrgUnit,
  listCiDevices,
  DeviceResponse,
  listCloudProviders,
  CloudProviderResponse,
  listCloudProviderResources,
  listCloudProviderResourceTypes,
  AwsResourceListResponse,
  AwsServiceTypeResponse,
  AssetProtocol,
  AssetRole,
  AssetStatus,
  listCMDBComponents,
  CMDBComponent,
  listSSPLayers,
  SSPLayer,
} from "../../api/frameworksApi";
import { showToast } from "../components/ui/custom-toast";
import { Tip } from "../components/ui/tooltip";
import { RBACAction, RBACMultiAction } from "../components/RBACButton";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { Breadcrumb } from "../components/Breadcrumb";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import {
  StatusBadge,
  parseOptionalPort,
  flattenOrgHierarchy,
} from "./cmdb/cmdbUtils";
import { InfraPicker, DeviceForm } from "./cmdb/components/InfraPicker";

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVICE_STATUS_OPTIONS: { value: ServiceStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "DEGRADED", label: "Degraded" },
  { value: "MAINTENANCE", label: "Maintenance" },
];

const ROLE_OPTIONS: { value: AssetRole; label: string }[] = [
  { value: "PRIMARY", label: "Primary" },
  { value: "BACKUP", label: "Backup" },
  { value: "STANDBY", label: "Standby" },
  { value: "READ_REPLICA", label: "Read Replica" },
  { value: "DR", label: "DR" },
  { value: "ACTIVE", label: "Active" },
  { value: "PASSIVE", label: "Passive" },
];

const PROTOCOL_OPTIONS = [
  { value: "TCP", label: "TCP" },
  { value: "UDP", label: "UDP" },
  { value: "HTTP", label: "HTTP" },
  { value: "HTTPS", label: "HTTPS" },
  { value: "GRPC", label: "gRPC" },
];

const DEP_STATUS_OPTIONS: { value: AssetStatus; label: string }[] = [
  { value: "RUNNING", label: "Running" },
  { value: "STOPPED", label: "Stopped" },
  { value: "STARTING", label: "Starting" },
  { value: "DEGRADED", label: "Degraded" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "UNKNOWN", label: "Unknown" },
];

const defaultDeviceForm: DeviceForm = {
  targetType: "DEVICE",
  deviceId: "",
  cloudProviderId: "",
  cloudResourceId: "",
  port: "",
  protocol: "TCP",
  role: "PRIMARY",
  status: "RUNNING",
};

const SERVICE_STATUS_BADGE: Record<string, string> = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25",
  INACTIVE: "bg-gray-500/15 text-gray-400 border border-gray-500/25",
  DEGRADED: "bg-red-500/15 text-red-400 border border-red-500/25",
  MAINTENANCE: "bg-amber-500/15 text-amber-400 border border-amber-500/25",
};

// ─── Service Component Panel helpers ─────────────────────────────────────────

function svcTypeIconBg(
  componentType?: string | null,
  hasCloud?: boolean,
): string {
  if (hasCloud) return "bg-indigo-500/20 text-indigo-600 dark:text-indigo-400";
  const t = (componentType || "").toUpperCase();
  if (t === "OPERATING_SYSTEM")
    return "bg-violet-500/20 text-violet-600 dark:text-violet-400";
  if (t === "DATABASE")
    return "bg-amber-500/20 text-amber-600 dark:text-amber-400";
  if (t === "MIDDLEWARE")
    return "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400";
  if (t === "APPLICATION")
    return "bg-pink-500/20 text-pink-600 dark:text-pink-400";
  return "bg-slate-500/20 text-slate-600 dark:text-slate-400";
}

function svcEffectiveSrc(comp: ServiceComponentMapping): string {
  const deps = comp.deployments ?? [];
  const hasCloud = deps.some((d) => d.ciType === "CLOUD");
  const hasOnPrem = deps.some((d) => d.ciType !== "CLOUD");
  if (hasOnPrem && hasCloud) return "MIXED";
  if (hasCloud) return "CLOUD";
  return "ON_PREM";
}

function svcSourceBadgeCls(label: string): string {
  if (label === "CLOUD")
    return "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/40";
  if (label === "MIXED")
    return "bg-violet-500/15 text-violet-600 dark:text-violet-400 border border-violet-500/40";
  return "bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-500/50";
}

function svcRolePillCls(role?: string | null): string {
  const r = (role || "").toUpperCase();
  if (r === "PRIMARY") return "bg-indigo-600 text-white";
  if (r === "BACKUP") return "bg-slate-500/30 text-slate-300";
  if (r === "STANDBY") return "bg-purple-500/20 text-purple-300";
  if (r === "READ_REPLICA") return "bg-cyan-500/20 text-cyan-300";
  if (r === "DR") return "bg-rose-500/20 text-rose-300";
  return "bg-indigo-600/80 text-white";
}

function svcAggregateStatus(deployments: any[]): string | null {
  if (!deployments.length) return null;
  if (deployments.some((d) => d.status?.toUpperCase() === "RUNNING"))
    return "RUNNING";
  return deployments[0]?.status ?? null;
}

function SvcStatusDot({ status }: { status?: string | null }) {
  const s = (status || "").toUpperCase();
  const dot =
    s === "RUNNING"
      ? "bg-emerald-500"
      : s === "STOPPED"
        ? "bg-red-500"
        : s === "DEGRADED"
          ? "bg-orange-500"
          : "bg-slate-500";
  const text =
    s === "RUNNING"
      ? "text-emerald-400"
      : s === "STOPPED"
        ? "text-red-400"
        : s === "DEGRADED"
          ? "text-orange-400"
          : "text-slate-400";
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : "Unknown";
  return (
    <div className={`flex items-center gap-1.5 ${text}`}>
      <span className="relative flex size-2 flex-shrink-0">
        {s === "RUNNING" && (
          <span
            className={`absolute inline-flex size-full rounded-full ${dot} opacity-50 animate-ping`}
          />
        )}
        <span className={`relative inline-flex size-2 rounded-full ${dot}`} />
      </span>
      <span className="text-xs font-semibold">{label}</span>
    </div>
  );
}

function SvcDetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-2.5 text-foreground">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <span className="text-xs font-semibold text-foreground">
        {value || "—"}
      </span>
    </div>
  );
}

function SvcMoreMenu({
  onRemove,
  removing,
}: {
  onRemove: () => void;
  removing?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-lg hover:bg-muted transition-colors d"
      >
        {removing ? (
          <Loader className="size-4 animate-spin" />
        ) : (
          <MoreVertical className="size-4" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 bg-popover border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
          <RBACAction module="cmdb" action="delete">
            <button
              onClick={() => {
                onRemove();
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
            >
              <Trash2 className="size-3.5" /> Delete
            </button>
          </RBACAction>
        </div>
      )}
    </div>
  );
}

// ─── Service Component Detail Panel ──────────────────────────────────────────

function ServiceComponentPanel({
  comp,
  onClose,
  onAddInfrastructure,
  onEditComponent,
  onEditDeployment,
  onRemoveDeployment,
  removingDeploymentIds,
}: {
  comp: ServiceComponentMapping;
  onClose: () => void;
  onAddInfrastructure: (c: ServiceComponentMapping) => void;
  onEditComponent?: (c: ServiceComponentMapping) => void;
  onEditDeployment: (d: ServiceComponentDeployment) => void;
  onRemoveDeployment: (d: ServiceComponentDeployment) => void;
  removingDeploymentIds: Record<string, boolean>;
}) {
  const deployments = comp.deployments ?? [];
  const hasCloud = deployments.some((d) => d.ciType === "CLOUD");
  const effectiveSrc = svcEffectiveSrc(comp);
  const iconClass = svcTypeIconBg(comp.component.componentType, hasCloud);

  return (
    <div
      className="flex flex-col bg-card"
      style={{ maxHeight: "calc(100vh - 2rem)" }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
        <div
          className={`flex-shrink-0 size-9 rounded-xl flex items-center justify-center ${iconClass}`}
        >
          {hasCloud ? (
            <Cloud className="size-4" />
          ) : (
            <Server className="size-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm text-foreground truncate">
              {comp.component.title}
            </span>
            {comp.component.componentType && (
              <span className="text-[10px] font-semibold bg-slate-100 dark:bg-muted text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 px-2 py-0.5 rounded-md tracking-wide">
                {comp.component.componentType}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors d flex-shrink-0"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <>
          {/* Component Summary */}
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">
              Component Summary
            </p>
            <div className="rounded-xl border border-border bg-muted/10 overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-border">
                <div className="p-2 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-foreground mb-2">
                    <Layers className="size-3.5" />
                    <span className="text-[10px] font-semibold">
                      Environment
                    </span>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-1 rounded-lg tracking-wide ${svcSourceBadgeCls(effectiveSrc)}`}
                  >
                    {effectiveSrc}
                  </span>
                </div>
                <div className="p-2 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-foreground mb-2">
                    <Box className="size-3.5" />
                    <span className="text-[10px] font-semibold">Assets</span>
                  </div>
                  <span className="text-xl font-bold text-foreground">
                    {deployments.length}
                  </span>
                </div>
                <div className="p-2 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-foreground mb-2">
                    <Activity className="size-3.5" />
                    <span className="text-[10px] font-semibold">Status</span>
                  </div>
                  {deployments.length > 0 ? (
                    <StatusBadge status={svcAggregateStatus(deployments)} />
                  ) : (
                    <span className="text-xs d">—</span>
                  )}
                </div>
                <div className="p-2 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-foreground mb-2">
                    <Layers className="size-3.5" />
                    <span className="text-[10px] font-semibold">
                      Component Layer
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="text-[11px] font-bold text-foreground">
                      {comp.sspLayerName || "—"}
                    </span>
                    {onEditComponent && (
                      <RBACAction module="cmdb" action="update">
                        <Tip label="Edit Component Layer" side="bottom">
                          <button
                            onClick={() => onEditComponent(comp)}
                            className="p-1 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 transition-colors"
                          >
                            <Edit2 className="size-3 hover:text-indigo-600" />
                          </button>
                        </Tip>
                      </RBACAction>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Connected Infrastructure */}
          {deployments.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-foreground">
                  Connected Infrastructure
                </p>
                <RBACMultiAction
                  checks={[
                    ["cmdb", "create"],
                    ["cmdb", "update"],
                  ]}
                  type="any"
                >
                  <button
                    onClick={() => onAddInfrastructure(comp)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-indigo-500/40 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/5 transition-colors"
                  >
                    <Plus className="size-3.5" /> Add Device
                  </button>
                </RBACMultiAction>
              </div>
              <div className="space-y-3">
                {deployments.map((d) => {
                  const isCloudDep = d.ciType === "CLOUD";
                  const name = isCloudDep
                    ? (d as any).cloudResourceName ||
                      d.awsResourceName ||
                      d.ciName ||
                      "Cloud Resource"
                    : d.ciName;
                  return (
                    <div
                      key={d.assetId}
                      className="rounded-xl border border-border bg-muted/10 overflow-hidden"
                    >
                      <div className="flex items-center gap-3 px-4 py-2">
                        <div className="flex-shrink-0 size-8 rounded-lg bg-slate-500/15 flex items-center justify-center">
                          {isCloudDep ? (
                            <Cloud className="size-4 text-indigo-600 dark:text-indigo-400" />
                          ) : (
                            <Server className="size-4 text-slate-500 dark:text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-foreground truncate">
                              {name}
                            </span>
                            <SvcStatusDot status={d.status} />
                          </div>
                          <span
                            className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-md ${svcRolePillCls(d.role)}`}
                          >
                            {d.role
                              ? d.role.charAt(0).toUpperCase() +
                                d.role.slice(1).toLowerCase()
                              : "Primary"}{" "}
                            Device
                          </span>
                        </div>
                      </div>
                      <div className="px-4 pb-1 border-t border-border/50">
                        {isCloudDep ? (
                          <>
                            <SvcDetailRow
                              icon={<Globe className="size-3.5" />}
                              label="Identifier"
                              value={
                                (d as any).cloudResourceIdentifier ||
                                d.awsResourceIdentifier ||
                                d.ciIdentifier
                              }
                            />
                            <SvcDetailRow
                              icon={<Layers className="size-3.5" />}
                              label="Service"
                              value={
                                (d as any).cloudServiceName ||
                                d.awsServiceName ||
                                d.ciName
                              }
                            />
                            <SvcDetailRow
                              icon={<Globe className="size-3.5" />}
                              label="Provider"
                              value={d.ciCloudProviderName}
                            />
                          </>
                        ) : (
                          <>
                            <SvcDetailRow
                              icon={<Globe className="size-3.5" />}
                              label="IP Address"
                              value={d.ciIpAddress || d.ciIdentifier}
                            />
                            <SvcDetailRow
                              icon={<Plug className="size-3.5" />}
                              label="Port"
                              value={d.port}
                            />
                            <SvcDetailRow
                              icon={<Shield className="size-3.5" />}
                              label="Protocol"
                              value={d.protocol}
                            />
                            <SvcDetailRow
                              icon={<Layers className="size-3.5" />}
                              label="Role"
                              value={d.role}
                            />
                            <SvcDetailRow
                              icon={<Box className="size-3.5" />}
                              label="Type"
                              value={comp.component.componentType}
                            />
                          </>
                        )}
                      </div>
                      <div className="flex gap-2 px-4 pb-2">
                        {!isCloudDep && (
                          <button
                            onClick={() => onEditDeployment(d)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20 transition-colors text-xs font-medium"
                          >
                            <Edit2 className="size-3.5" /> Edit
                          </button>
                        )}
                        <RBACAction module="cmdb" action="delete">
                          <button
                            onClick={() => onRemoveDeployment(d)}
                            disabled={removingDeploymentIds[d.assetId]}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors text-xs font-medium disabled:opacity-50"
                          >
                            {removingDeploymentIds[d.assetId] ? (
                              <Loader className="size-3.5 animate-spin" />
                            ) : (
                              <>
                                <Trash2 className="size-3.5" /> Delete
                              </>
                            )}
                          </button>
                        </RBACAction>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      </div>

      {/* Footer: only show Add Device button when no deployments (button is in heading row otherwise) */}
      {deployments.length === 0 && (
        <div className="border-t border-border p-4 flex-shrink-0">
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={() => onAddInfrastructure(comp)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-indigo-500/40 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/5 transition-colors"
            >
              <Plus className="size-3.5" /> Add Device
            </button>
          </RBACMultiAction>
        </div>
      )}
    </div>
  );
}

// ─── Service Components List ──────────────────────────────────────────────────

function ServiceComponentsList({
  components,
  onRemoveComponent,
  removingCompIds,
  selectedComp,
  onSelectComp,
}: {
  components: ServiceComponentMapping[];
  onRemoveComponent: (comp: ServiceComponentMapping) => void;
  removingCompIds: Record<string, boolean>;
  selectedComp: ServiceComponentMapping | null;
  onSelectComp: (comp: ServiceComponentMapping | null) => void;
}) {
  if (components.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center border border-dashed border-border rounded-xl bg-muted/10">
        <div className="size-12 rounded-xl bg-muted/60 flex items-center justify-center">
          <Server className="size-6 d/40" />
        </div>
        <p className="text-sm font-medium d">No components linked yet</p>
        <p className="text-xs d/60">
          Add a component to define what runs in this service.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 min-[1700px]:grid-cols-2 gap-2.5">
      {components.map((comp) => {
        const deployments = comp.deployments ?? [];
        const hasCloud = deployments.some((d) => d.ciType === "CLOUD");
        const iconClass = svcTypeIconBg(comp.component.componentType, hasCloud);
        const effectiveSrc = svcEffectiveSrc(comp);
        const isSelected = selectedComp?.id === comp.id;

        return (
          <Tip
            key={comp.id}
            label={isSelected ? "Click to close details" : "Click to view details"}
            side="top"
            className="block w-full h-[94px]"
          >
          <div
            onClick={() => onSelectComp(isSelected ? null : comp)}
            className={`group block w-full h-full rounded-xl border bg-card cursor-pointer transition-all overflow-hidden ${
              isSelected
                ? "border-indigo-500/60 bg-indigo-500/5 shadow-[0_0_0_1px_rgba(99,102,241,0.2)]"
                : "border-border hover:border-indigo-500/40 hover:bg-indigo-500/5 hover:shadow-md"
            }`}
          >
            <div className="flex items-center gap-3 px-4 py-3.5">
              <div
                className={`flex-shrink-0 size-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 ${iconClass}`}
              >
                {hasCloud ? (
                  <Cloud className="size-5" />
                ) : (
                  <Server className="size-5" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`font-semibold text-sm transition-colors truncate whitespace-nowrap min-w-0 ${isSelected ? "text-indigo-600 dark:text-indigo-400" : "text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400"}`}
                    title={comp.component.title}
                  >
                    {comp.component.title}
                  </span>
                  {comp.component.componentType && (
                    <span
                      className="text-[10px] font-semibold bg-slate-100 dark:bg-muted text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 px-2 py-0.5 rounded-md tracking-wide whitespace-nowrap max-w-[120px] truncate flex-shrink-0"
                      title={comp.component.componentType}
                    >
                      {comp.component.componentType}
                    </span>
                  )}
                </div>
                <p className="text-xs d mt-0.5">
                  {deployments.length} asset
                  {deployments.length !== 1 ? "s" : ""}
                </p>
                {comp.sspLayerName && (
                  <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-violet-600 dark:text-violet-400">
                    <Layers className="size-3 flex-shrink-0" />
                    <span className="truncate" title={comp.sspLayerName}>
                      {comp.sspLayerName}
                    </span>
                  </div>
                )}
              </div>

              <span
                className={`flex-shrink-0 whitespace-nowrap text-[10px] font-medium text-indigo-600 dark:text-indigo-400 transition-all duration-200 ${
                  isSelected
                    ? "opacity-0 w-0 overflow-hidden"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              >
                View details
              </span>

              <span
                className={`flex-shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-lg tracking-wide ${svcSourceBadgeCls(effectiveSrc)}`}
              >
                {effectiveSrc}
              </span>

              <ChevronRight
                className={`size-4 flex-shrink-0 transition-all duration-200 ${
                  isSelected
                    ? "text-indigo-600 dark:text-indigo-400 rotate-90"
                    : "d group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:translate-x-0.5"
                }`}
              />

              <span onClick={(e) => e.stopPropagation()}>
                <SvcMoreMenu
                  onRemove={() => onRemoveComponent(comp)}
                  removing={removingCompIds[comp.id]}
                />
              </span>
            </div>
          </div>
          </Tip>
        );
      })}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CMDBServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [service, setService] = useState<ServiceResponse | null>(null);
  // Publish the open service, so cmdb.yaml's {item} questions apply. The SERVICES view comes from the
  // route. Cleared on unmount.
  useEffect(() => {
    const name = service?.name;
    useUiContextStore
      .getState()
      .setSelectedItem(id && name ? { id, name } : null);
    return () => useUiContextStore.getState().clearSelectedItem();
  }, [id, service]);
  const [loading, setLoading] = useState(true);

  // Dropdown data
  const [orgOptions, setOrgOptions] = useState<OrgUnit[]>([]);
  const [devices, setDevices] = useState<DeviceResponse[]>([]);
  const [cloudAccounts, setCloudAccounts] = useState<CloudProviderResponse[]>(
    [],
  );
  const [components, setComponents] = useState<CMDBComponent[]>([]);
  const [sspLayers, setSspLayers] = useState<SSPLayer[]>([]);
  const [awsResources, setAwsResources] = useState<AwsResourceListResponse[]>(
    [],
  );
  const [loadingAwsRes, setLoadingAwsRes] = useState(false);
  const [awsResTypes, setAwsResTypes] = useState<AwsServiceTypeResponse[]>([]);
  const [awsResTypeFilter, setAwsResTypeFilter] = useState("");
  const [awsResSearch, setAwsResSearch] = useState("");

  // Edit service
  const [showEditForm, setShowEditForm] = useState(false);
  const [editForm, setEditForm] = useState({
    orgId: "",
    name: "",
    status: "ACTIVE" as ServiceStatus,
    description: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  // Delete service
  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add-component wizard
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState<"pick-component" | "add-device">(
    "pick-component",
  );
  const [selectedComponent, setSelectedComponent] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [wizardLayerId, setWizardLayerId] = useState("");
  const [wizardLayerManuallyChanged, setWizardLayerManuallyChanged] =
    useState(false);
  const [deviceForm, setDeviceForm] = useState<DeviceForm>(defaultDeviceForm);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [isSavingWizard, setIsSavingWizard] = useState(false);

  // Add device to existing component
  const [addDeviceTarget, setAddDeviceTarget] =
    useState<ServiceComponentMapping | null>(null);
  const [addDeviceForm, setAddDeviceForm] =
    useState<DeviceForm>(defaultDeviceForm);
  const [addDeviceSearch, setAddDeviceSearch] = useState("");
  const [isSavingDevice, setIsSavingDevice] = useState(false);

  // Edit component mapping (layer + reason)
  const [editingMapping, setEditingMapping] =
    useState<ServiceComponentMapping | null>(null);
  const [editLayerId, setEditLayerId] = useState("");
  const [editLayerReason, setEditLayerReason] = useState("");
  const [isSavingMapping, setIsSavingMapping] = useState(false);

  // Edit deployment
  const [editDepTarget, setEditDepTarget] =
    useState<ServiceComponentDeployment | null>(null);
  const [editDepForm, setEditDepForm] = useState({
    port: "",
    protocol: "TCP" as AssetProtocol,
    role: "PRIMARY" as AssetRole,
    status: "RUNNING" as AssetStatus,
  });
  const [isSavingDep, setIsSavingDep] = useState(false);

  // Remove
  const [deletingComp, setDeletingComp] =
    useState<ServiceComponentMapping | null>(null);
  const [deletingDep, setDeletingDep] =
    useState<ServiceComponentDeployment | null>(null);
  const [isRemovingComp, setIsRemovingComp] = useState<Record<string, boolean>>(
    {},
  );
  const [isRemovingDep, setIsRemovingDep] = useState<Record<string, boolean>>(
    {},
  );

  // Selected component for inline panel
  const [selectedComp, setSelectedComp] =
    useState<ServiceComponentMapping | null>(null);

  // Sync selectedComp when service data refreshes
  useEffect(() => {
    const list = service?.components ?? [];
    setSelectedComp((prev) => {
      if (prev) {
        const updated = list.find((c) => c.id === prev.id);
        return updated ?? list[0] ?? null;
      }
      return list[0] ?? null;
    });
  }, [service]);

  // ─── Load ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (id) loadAll(id);
  }, [id]);

  async function loadAll(serviceId: string) {
    setLoading(true);
    try {
      const [svcRes, orgRes, devRes, cloudRes, compRes, layerRes] =
        await Promise.all([
          getServiceById(serviceId),
          listOrgUnits().catch(() => ({ data: [] as OrgUnit[] })),
          listCiDevices().catch(() => ({ data: [] as DeviceResponse[] })),
          listCloudProviders().catch(() => ({
            data: [] as CloudProviderResponse[],
          })),
          listCMDBComponents().catch(() => ({ data: [] as CMDBComponent[] })),
          listSSPLayers().catch(() => ({ data: [] as SSPLayer[] })),
        ]);
      setService(svcRes.data);
      setOrgOptions(flattenOrgHierarchy(orgRes.data ?? [], ["ORGANIZATION"]));
      setDevices(devRes.data ?? []);
      setCloudAccounts(cloudRes.data ?? []);
      setComponents(compRes.data ?? []);
      setSspLayers(
        (layerRes.data ?? [])
          .filter((l) => l.active !== false)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
      );
    } catch {
      showToast("error", "Failed to load service");
    } finally {
      setLoading(false);
    }
  }

  function findLayerIdForComponent(c: CMDBComponent): string {
    if (!sspLayers.length) return "";
    const anyC = c as any;
    if (anyC.layerId && sspLayers.some((l) => l.id === anyC.layerId))
      return anyC.layerId;
    const compLayerId = anyC.layer?.id;
    if (compLayerId && sspLayers.some((l) => l.id === compLayerId))
      return compLayerId;
    const type = (c.componentType || "").toLowerCase();
    if (!type) return "";
    const match = sspLayers.find((l) => {
      const slug = (l.slug || "").toLowerCase();
      const name = (l.displayName || "").toLowerCase();
      return (
        slug === type ||
        name === type ||
        slug.includes(type) ||
        type.includes(slug)
      );
    });
    return match?.id ?? "";
  }

  async function reloadService() {
    if (!id) return;
    try {
      const res = await getServiceById(id);
      setService(res.data);
    } catch {
      /* ignore */
    }
  }

  async function loadAwsResources(
    cloudProviderId: string,
    typeFilter?: string,
    q?: string,
  ) {
    setLoadingAwsRes(true);
    try {
      const res = await listCloudProviderResources(cloudProviderId, {
        cloudIdentifier: typeFilter || undefined,
        q: q || undefined,
      });
      setAwsResources(res.data ?? []);
    } catch {
      setAwsResources([]);
    } finally {
      setLoadingAwsRes(false);
    }
  }

  async function loadAwsResourceTypes(cloudProviderId: string) {
    try {
      const res = await listCloudProviderResourceTypes(cloudProviderId);
      setAwsResTypes(res.data ?? []);
    } catch {
      setAwsResTypes([]);
    }
  }

  const activeCloudProviderId =
    (deviceForm.targetType === "CLOUD" && deviceForm.cloudProviderId) ||
    (addDeviceForm.targetType === "CLOUD" && addDeviceForm.cloudProviderId) ||
    "";

  useEffect(() => {
    if (!activeCloudProviderId) return;
    const t = setTimeout(
      () =>
        loadAwsResources(activeCloudProviderId, awsResTypeFilter, awsResSearch),
      300,
    );
    return () => clearTimeout(t);
  }, [awsResTypeFilter, awsResSearch, activeCloudProviderId]);

  function handleCloudAccountChange(providerId: string) {
    setAwsResTypeFilter("");
    if (providerId) {
      loadAwsResourceTypes(providerId);
      loadAwsResources(providerId);
    }
  }

  // ─── Edit Service ────────────────────────────────────────────────────────

  function openEdit() {
    if (!service) return;
    setEditForm({
      orgId: service.orgId,
      name: service.name,
      status: service.status,
      description: service.description ?? "",
    });
    setShowEditForm(true);
  }

  async function handleSaveEdit() {
    if (!service || !editForm.orgId) {
      showToast("error", "Org unit is required");
      return;
    }
    if (!editForm.name.trim()) {
      showToast("error", "Name is required");
      return;
    }
    setIsSaving(true);
    try {
      const res = await updateService(service.id, {
        orgId: editForm.orgId,
        name: editForm.name.trim(),
        status: editForm.status,
        description: editForm.description || undefined,
      });
      setService(res.data);
      showToast("success", "Service updated");
      setShowEditForm(false);
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }

  // ─── Delete Service ───────────────────────────────────────────────────────

  async function handleDelete() {
    if (!service) return;
    setIsDeleting(true);
    try {
      await deleteService(service.id);
      showToast("success", "Service deleted");
      navigate("/cmdb/services");
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to delete",
      );
    } finally {
      setIsDeleting(false);
    }
  }

  // ─── Add Component Wizard ─────────────────────────────────────────────────

  function openWizard() {
    setWizardStep("pick-component");
    setSelectedComponent(null);
    setWizardLayerId("");
    setWizardLayerManuallyChanged(false);
    setDeviceForm(defaultDeviceForm);
    setDeviceSearch("");
    setAwsResources([]);
    setAwsResTypes([]);
    setAwsResTypeFilter("");
    setAwsResSearch("");
    setShowWizard(true);
  }

  function resetWizard() {
    setShowWizard(false);
    setSelectedComponent(null);
    setWizardLayerId("");
    setWizardLayerManuallyChanged(false);
  }

  async function handleWizardSave() {
    if (!service || !selectedComponent) return;
    const port = parseOptionalPort(deviceForm.port);
    if (port === undefined) {
      showToast("error", "Port must be a number between 1 and 65535");
      return;
    }

    // Send layer override only when user picked a layer that differs from
    // the component's auto-suggested one. The API requires a reason with
    // layerSlug — use a canned reason since we don't prompt the user on Add.
    const pickedComp = components.find((c) => c.id === selectedComponent.id);
    const suggestedLayerId = pickedComp
      ? findLayerIdForComponent(pickedComp)
      : "";
    const isLayerOverride =
      wizardLayerManuallyChanged &&
      !!wizardLayerId &&
      wizardLayerId !== suggestedLayerId;

    setIsSavingWizard(true);
    try {
      const addPayload: {
        componentId: string;
        layerSlug?: string;
        layerOverrideReason?: string;
      } = { componentId: selectedComponent.id };
      if (isLayerOverride) {
        const layer = sspLayers.find((l) => l.id === wizardLayerId);
        if (layer?.slug) {
          addPayload.layerSlug = layer.slug;
          addPayload.layerOverrideReason =
            "Layer set during component add";
        }
      }
      try {
        await addServiceComponent(service.id, addPayload);
      } catch (err: any) {
        if (!String(err.message).includes("409")) throw err;
      }

      const hasTarget =
        (deviceForm.targetType === "DEVICE" && deviceForm.deviceId) ||
        (deviceForm.targetType === "CLOUD" &&
          deviceForm.cloudProviderId &&
          deviceForm.cloudResourceId);

      if (hasTarget) {
        const assetPayload: any = {
          componentId: selectedComponent.id,
          port: port || undefined,
          protocol: deviceForm.protocol || undefined,
          role: deviceForm.role,
          status: deviceForm.status,
        };
        if (deviceForm.targetType === "DEVICE") {
          assetPayload.ciType = "ON_PREMISES";
          assetPayload.ciOnpremId = deviceForm.deviceId;
        } else {
          assetPayload.ciType = "CLOUD";
          assetPayload.ciCloudProviderId = deviceForm.cloudProviderId;
          assetPayload.cloudResourceId = deviceForm.cloudResourceId;
        }
        const assetRes = await createAssetDeployment(assetPayload);
        await linkAssetToService(service.id, assetRes.data.id);
      }

      showToast(
        "success",
        hasTarget ? "Component and infrastructure added" : "Component added",
      );
      resetWizard();
      await reloadService();
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to add component",
      );
    } finally {
      setIsSavingWizard(false);
    }
  }

  // ─── Add Device to Component ──────────────────────────────────────────────

  function openAddDevice(comp: ServiceComponentMapping) {
    setAddDeviceTarget(comp);
    setAddDeviceForm(defaultDeviceForm);
    setAddDeviceSearch("");
    setAwsResources([]);
    setAwsResTypes([]);
    setAwsResTypeFilter("");
    setAwsResSearch("");
  }

  async function handleSaveAddDevice() {
    const f = addDeviceForm;
    const hasTarget =
      (f.targetType === "DEVICE" && f.deviceId) ||
      (f.targetType === "CLOUD" && f.cloudProviderId && f.cloudResourceId);
    if (!addDeviceTarget || !hasTarget) {
      showToast(
        "error",
        f.targetType === "DEVICE"
          ? "Select a device"
          : "Select a cloud account and resource",
      );
      return;
    }
    const port = parseOptionalPort(f.port);
    if (port === undefined) {
      showToast("error", "Port must be a number between 1 and 65535");
      return;
    }

    setIsSavingDevice(true);
    try {
      const assetPayload: any = {
        componentId: addDeviceTarget.componentId,
        port: port || undefined,
        protocol: f.protocol || undefined,
        role: f.role,
        status: f.status,
      };
      if (f.targetType === "DEVICE") {
        assetPayload.ciType = "ON_PREMISES";
        assetPayload.ciOnpremId = f.deviceId;
      } else {
        assetPayload.ciType = "CLOUD";
        assetPayload.ciCloudProviderId = f.cloudProviderId;
        assetPayload.cloudResourceId = f.cloudResourceId;
      }
      const assetRes = await createAssetDeployment(assetPayload);
      await linkAssetToService(service!.id, assetRes.data.id);
      showToast("success", "Infrastructure added");
      setAddDeviceTarget(null);
      await reloadService();
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to add Device",
      );
    } finally {
      setIsSavingDevice(false);
    }
  }

  // ─── Edit Component Mapping (Layer + Reason) ──────────────────────────────

  function openEditComponent(comp: ServiceComponentMapping) {
    const currentSlug = (comp.sspLayerSlug ?? "").toLowerCase();
    const preselect = currentSlug
      ? (sspLayers.find((l) => l.slug?.toLowerCase() === currentSlug)?.id ?? "")
      : "";
    setEditingMapping(comp);
    setEditLayerId(preselect);
    setEditLayerReason("");
  }

  function closeEditComponent() {
    if (isSavingMapping) return;
    setEditingMapping(null);
    setEditLayerId("");
    setEditLayerReason("");
  }

  async function handleSaveEditComponent() {
    if (!editingMapping) return;

    const newLayer = sspLayers.find((l) => l.id === editLayerId);
    const newSlug = (newLayer?.slug ?? "").toLowerCase();
    const originalSlug = (editingMapping.sspLayerSlug ?? "").toLowerCase();
    const layerChanged = !!newSlug && newSlug !== originalSlug;

    if (!layerChanged) {
      showToast("info", "No changes to save");
      return;
    }

    const reason = editLayerReason.trim();
    if (!reason) {
      showToast("error", "Enter a reason for changing the component layer");
      return;
    }

    setIsSavingMapping(true);
    try {
      await updateServiceComponentMapping(editingMapping.id, {
        layerSlug: newSlug,
        layerOverrideReason: reason,
      });
      showToast("success", "Component layer updated");
      setEditingMapping(null);
      await reloadService();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("CMDB-0056-E")) {
        showToast("error", "Selected layer not found. Refresh and try again.");
      } else if (msg.includes("GRC-0001-E")) {
        showToast(
          "error",
          "A reason is required when changing the component layer.",
        );
      } else if (msg.includes("CMDB-0028-E") || msg.includes("404")) {
        showToast("error", "This component mapping no longer exists.");
      } else {
        showToast(
          "error",
          err instanceof Error ? err.message : "Failed to update component",
        );
      }
    } finally {
      setIsSavingMapping(false);
    }
  }

  // ─── Remove Component ─────────────────────────────────────────────────────

  async function confirmRemoveComp() {
    if (!deletingComp) return;
    setIsRemovingComp((p) => ({ ...p, [deletingComp.id]: true }));
    try {
      await removeServiceComponent(deletingComp.id);
      await reloadService();
      showToast("success", "Component removed");
      setDeletingComp(null);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to remove component",
      );
    } finally {
      setIsRemovingComp((p) => ({ ...p, [deletingComp!.id]: false }));
    }
  }

  // ─── Edit Deployment ──────────────────────────────────────────────────────

  function openEditDep(dep: ServiceComponentDeployment) {
    setEditDepTarget(dep);
    setEditDepForm({
      port: dep.port != null ? String(dep.port) : "",
      protocol: (dep.protocol as AssetProtocol) ?? "TCP",
      role: (dep.role as AssetRole) ?? "PRIMARY",
      status: (dep.status as AssetStatus) ?? "RUNNING",
    });
  }

  async function handleSaveEditDep() {
    if (!editDepTarget) return;
    const port = parseOptionalPort(editDepForm.port);
    if (port === undefined) {
      showToast("error", "Port must be a number between 1 and 65535");
      return;
    }
    setIsSavingDep(true);
    try {
      await updateAssetDeployment(editDepTarget.assetId, {
        port: port || null,
        protocol: editDepForm.protocol,
        role: editDepForm.role,
        status: editDepForm.status,
      });
      showToast("success", "Updated");
      await reloadService();
      setEditDepTarget(null);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update",
      );
    } finally {
      setIsSavingDep(false);
    }
  }

  // ─── Remove Deployment ────────────────────────────────────────────────────

  async function confirmRemoveDep() {
    if (!deletingDep) return;
    setIsRemovingDep((p) => ({ ...p, [deletingDep.assetId]: true }));
    try {
      await removeServiceAsset(deletingDep.assetId);
      await reloadService();
      showToast("success", "Infrastructure removed");
      setDeletingDep(null);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to remove",
      );
    } finally {
      setIsRemovingDep((p) => ({ ...p, [deletingDep!.assetId]: false }));
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="size-8 animate-spin d" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-6 text-center d">
        Service not found.
        <button
          onClick={() => navigate("/cmdb/services")}
          className="ml-2 text-indigo-600 hover:underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const comps = service.components ?? [];

  return (
    <div className="space-y-4">
      <Breadcrumb
        parent="Services"
        parentTo="/cmdb/services"
        current={service.name}
        actions={
          <>
            <button
              onClick={openEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-indigo-500/40 bg-indigo-500/10 rounded-lg hover:bg-indigo-500/20 hover:border-indigo-500/60 transition-colors text-sm text-indigo-600 dark:text-indigo-400 font-medium"
            >
              <Edit2 className="size-4" /> Edit
            </button>
            <RBACAction module="cmdb" action="delete">
              <button
                onClick={() => setShowDelete(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-red-500/40 bg-red-500/10 rounded-lg hover:bg-red-500/20 hover:border-red-500/60 transition-colors text-sm text-red-600 dark:text-red-400 font-medium"
              >
                <Trash2 className="size-4" /> Delete
              </button>
            </RBACAction>
          </>
        }
      />

      {/* Hero banner — single row of 4 cards */}
      <div className="relative rounded-2xl overflow-hidden border border-indigo-500/10 bg-gradient-to-br from-indigo-500/5 via-transparent to-violet-500/5">
        <div className="absolute -top-8 -left-8 size-36 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -right-8 size-36 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative px-6 py-4">
          <div className="grid grid-cols-4 gap-3">
            {/* Name card */}
            <div
              className="flex items-center gap-3 rounded-xl bg-muted/30 border border-border border-b-2 border-b-indigo-500 px-4 py-3"
              style={{ boxShadow: "0 4px 16px -4px rgba(99,102,241,0.35)" }}
            >
              <div className="flex-shrink-0 size-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-violet-500/20 border border-indigo-500/20 flex items-center justify-center">
                <Server className="size-4 text-indigo-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-foreground tracking-wider">
                  Service
                </p>
                <Tip
                  label={service.name}
                  side="top"
                  className="block min-w-0"
                >
                  <p className="text-sm font-bold text-foreground truncate">
                    {service.name}
                  </p>
                </Tip>
                <span
                  className={`mt-0.5 inline-flex px-2 py-0 rounded text-[10px] font-semibold ${SERVICE_STATUS_BADGE[service.status] ?? "bg-gray-500/15 text-gray-400 border border-gray-500/25"}`}
                >
                  {service.status}
                </span>
              </div>
            </div>

            {/* Created */}
            <div
              className="flex items-center gap-3 rounded-xl bg-muted/30 border border-border border-b-2 border-b-violet-500 px-4 py-3"
              style={{ boxShadow: "0 4px 16px -4px rgba(139,92,246,0.35)" }}
            >
              <div className="flex-shrink-0 size-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <Calendar className="size-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-foreground tracking-wider">
                  Created
                </p>
                <p className="text-sm font-semibold text-foreground truncate">
                  {new Date(service.createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>

            {/* Updated */}
            <div
              className="flex items-center gap-3 rounded-xl bg-muted/30 border border-border border-b-2 border-b-violet-500 px-4 py-3"
              style={{ boxShadow: "0 4px 16px -4px rgba(139,92,246,0.35)" }}
            >
              <div className="flex-shrink-0 size-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Clock className="size-4 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-foreground tracking-wider">
                  Updated
                </p>
                <p className="text-sm font-semibold text-foreground truncate">
                  {new Date(service.updatedAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>

            {/* Components */}
            <div
              className="flex items-center gap-3 rounded-xl bg-muted/30 border border-border border-b-2 border-b-sky-500 px-4 py-3"
              style={{ boxShadow: "0 4px 16px -4px rgba(14,165,233,0.35)" }}
            >
              <div className="flex-shrink-0 size-8 rounded-lg bg-sky-500/10 flex items-center justify-center">
                <Layers className="size-4 text-sky-600 dark:text-sky-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-foreground tracking-wider">
                  Components
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {comps.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Components section */}
      <div className="space-y-4">
        {/* Header bar — full width */}
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">
            Components
            <span className="ml-2 text-sm font-normal d">({comps.length})</span>
          </h2>
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={openWizard}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
            >
              <Plus className="size-4" /> New Component
            </button>
          </RBACMultiAction>
        </div>

        {/* Split pane: list left, detail right. Stacks vertically below lg. */}
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <div className="order-2 lg:order-1 flex-1 min-w-0 w-full lg:self-start">
            <ServiceComponentsList
              components={comps}
              onRemoveComponent={setDeletingComp}
              removingCompIds={isRemovingComp}
              selectedComp={selectedComp}
              onSelectComp={setSelectedComp}
            />
          </div>
          <div
            className={`${selectedComp ? "flex" : "hidden lg:flex"} order-1 lg:order-2 w-full lg:w-[clamp(420px,38vw,640px)] flex-shrink-0 lg:sticky lg:top-4 rounded-2xl overflow-hidden transition-all duration-300 flex-col max-h-[75vh] lg:max-h-[calc(100vh-2rem)] ${
              selectedComp
                ? "border border-slate-200 dark:border-slate-700/60 shadow-lg shadow-slate-200/80 dark:shadow-black/20"
                : "border border-dashed border-indigo-500/40 shadow-[0_0_24px_-4px_rgba(99,102,241,0.2)]"
            }`}
          >
            {selectedComp ? (
              <ServiceComponentPanel
                comp={selectedComp}
                onClose={() => setSelectedComp(null)}
                onAddInfrastructure={openAddDevice}
                onEditComponent={openEditComponent}
                onEditDeployment={openEditDep}
                onRemoveDeployment={setDeletingDep}
                removingDeploymentIds={isRemovingDep}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 w-full gap-3 text-center p-8 bg-indigo-500/[0.03]">
                <div className="size-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                  <Layers className="size-6 text-indigo-400/60" />
                </div>
                <p className="text-sm font-semibold text-foreground/60">
                  Select a component
                </p>
                <p className="text-xs d/50">
                  Click any component on the left to view its details here.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Edit Service Modal ───────────────────────────────────────────────── */}
      <Modal
        open={showEditForm}
        onClose={() => {
          if (!isSaving) setShowEditForm(false);
        }}
        title="Edit Service"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowEditForm(false)}
            onSubmit={handleSaveEdit}
            submitLabel={isSaving ? "Saving…" : "Save"}
            submitDisabled={isSaving}
          />
        }
      >
        <div className="space-y-4">
          <FormField label="Org Unit" required>
            <FormSelect
              value={editForm.orgId}
              onChange={(v) => setEditForm({ ...editForm, orgId: v })}
              options={[
                { value: "", label: "Select org unit…" },
                ...orgOptions.map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          </FormField>
          <FormField label="Name" required>
            <FormInput
              value={editForm.name}
              onChange={(v) => setEditForm({ ...editForm, name: v })}
              placeholder="e.g. MySQL Cluster"
            />
          </FormField>
          <FormField label="Status" required>
            <FormSelect
              value={editForm.status}
              onChange={(v) =>
                setEditForm({ ...editForm, status: v as ServiceStatus })
              }
              options={SERVICE_STATUS_OPTIONS}
            />
          </FormField>
          <FormField label="Description">
            <FormTextarea
              value={editForm.description}
              onChange={(v) => setEditForm({ ...editForm, description: v })}
              placeholder="Optional description"
              rows={3}
            />
          </FormField>
        </div>
      </Modal>

      {/* ── Add Component Wizard ─────────────────────────────────────────────── */}
      <Modal
        open={showWizard}
        onClose={resetWizard}
        title={
          wizardStep === "pick-component"
            ? "Add Component · Step 1 of 2: Pick Component"
            : "Add Component · Step 2 of 2: Add Device (optional)"
        }
        size="md"
      >
        <div className="space-y-4">
          {wizardStep === "pick-component" && (
            <div className="space-y-4">
              <FormField label="Component" required>
                <FormSelect
                  value={selectedComponent?.id || ""}
                  onChange={(cid) => {
                    const c = components.find((x) => x.id === cid);
                    if (!c) return;
                    setSelectedComponent({ id: c.id, title: c.title });
                    if (!wizardLayerManuallyChanged) {
                      const suggested = findLayerIdForComponent(c);
                      setWizardLayerId(
                        suggested || (sspLayers[0]?.id ?? ""),
                      );
                    }
                    setWizardStep("add-device");
                  }}
                  options={components.map((c) => ({
                    value: c.id,
                    label: c.componentType
                      ? `${c.title} (${c.componentType})`
                      : c.title,
                  }))}
                  placeholder="Select a component"
                />
              </FormField>
              <div className="flex justify-end pt-2 border-t border-border">
                <button
                  onClick={resetWizard}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {wizardStep === "add-device" && selectedComponent && (
            <div className="space-y-4">
              <button
                onClick={() => setWizardStep("pick-component")}
                className="flex items-center gap-1.5 text-sm text-indigo-600 hover:underline"
              >
                <ArrowLeft className="size-3.5" /> Back
              </button>
              <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
                Component:{" "}
                <span className="font-semibold">{selectedComponent.title}</span>
              </div>
              <FormField label="Component Layer" required>
                <FormSelect
                  value={wizardLayerId}
                  onChange={(v) => {
                    setWizardLayerId(v);
                    setWizardLayerManuallyChanged(true);
                  }}
                  placeholder={
                    sspLayers.length === 0
                      ? "No layers available"
                      : "Select a layer"
                  }
                  options={sspLayers.map((l) => ({
                    value: l.id,
                    label: l.displayName,
                  }))}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Auto-selected from the picked component — change it if
                  needed.
                </p>
              </FormField>
              <InfraPicker
                search={deviceSearch}
                onSearch={setDeviceSearch}
                form={deviceForm}
                onForm={setDeviceForm}
                devices={devices}
                cloudAccounts={cloudAccounts}
                awsResTypes={awsResTypes}
                awsResTypeFilter={awsResTypeFilter}
                onResTypeFilter={setAwsResTypeFilter}
                awsResSearch={awsResSearch}
                onResSearch={setAwsResSearch}
                awsResources={awsResources}
                loadingAwsRes={loadingAwsRes}
                onCloudAccountChange={handleCloudAccountChange}
              />
              <p className="text-xs d">
                Infrastructure selection is optional — you can add it later.
              </p>
              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <button
                  onClick={resetWizard}
                  disabled={isSavingWizard}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWizardSave}
                  disabled={
                    isSavingWizard ||
                    (sspLayers.length > 0 && !wizardLayerId)
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
                >
                  {isSavingWizard && (
                    <Loader className="size-3.5 animate-spin" />
                  )}
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Add Device to Existing Component ─────────────────────────────────── */}
      <Modal
        open={!!addDeviceTarget}
        onClose={() => {
          if (!isSavingDevice) setAddDeviceTarget(null);
        }}
        title={`Add Device — ${addDeviceTarget?.component.title ?? ""}`}
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setAddDeviceTarget(null)}
            onSubmit={handleSaveAddDevice}
            submitLabel={isSavingDevice ? "Saving…" : "Save"}
            submitDisabled={
              isSavingDevice ||
              (addDeviceForm.targetType === "DEVICE"
                ? !addDeviceForm.deviceId
                : !addDeviceForm.cloudResourceId)
            }
          />
        }
      >
        <InfraPicker
          search={addDeviceSearch}
          onSearch={setAddDeviceSearch}
          form={addDeviceForm}
          onForm={setAddDeviceForm}
          devices={devices}
          cloudAccounts={cloudAccounts}
          awsResTypes={awsResTypes}
          awsResTypeFilter={awsResTypeFilter}
          onResTypeFilter={setAwsResTypeFilter}
          awsResSearch={awsResSearch}
          onResSearch={setAwsResSearch}
          awsResources={awsResources}
          loadingAwsRes={loadingAwsRes}
          onCloudAccountChange={handleCloudAccountChange}
        />
      </Modal>

      {/* ── Edit Component Mapping Modal ─────────────────────────────────────── */}
      <Modal
        open={!!editingMapping}
        onClose={closeEditComponent}
        title="Update Component"
        size="md"
      >
        {editingMapping && (
          <div className="space-y-4">
            <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
              Component:{" "}
              <span className="font-semibold">
                {editingMapping.component.title}
              </span>
              {editingMapping.component.componentType && (
                <span className="ml-2 text-[10px] font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
                  {editingMapping.component.componentType}
                </span>
              )}
            </div>
            <FormField label="Component Layer" required>
              <FormSelect
                value={editLayerId}
                onChange={setEditLayerId}
                placeholder={
                  sspLayers.length === 0
                    ? "No layers available"
                    : "Select a layer"
                }
                options={sspLayers.map((l) => ({
                  value: l.id,
                  label: l.displayName,
                }))}
              />
            </FormField>
            {(() => {
              const newSlug = (
                sspLayers.find((l) => l.id === editLayerId)?.slug ?? ""
              ).toLowerCase();
              const originalSlug = (
                editingMapping.sspLayerSlug ?? ""
              ).toLowerCase();
              const layerChanged = !!newSlug && newSlug !== originalSlug;
              if (!layerChanged) return null;
              return (
                <FormField label="Reason" required>
                  <FormTextarea
                    value={editLayerReason}
                    onChange={setEditLayerReason}
                    placeholder="Explain why this component's layer is being reclassified"
                    rows={3}
                  />
                </FormField>
              );
            })()}
            <div className="flex justify-end gap-3 pt-2 border-t border-border">
              <button
                onClick={closeEditComponent}
                disabled={isSavingMapping}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditComponent}
                disabled={isSavingMapping || !editLayerId}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
              >
                {isSavingMapping && (
                  <Loader className="size-3.5 animate-spin" />
                )}
                Save
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Edit Deployment Modal ─────────────────────────────────────────────── */}
      <Modal
        open={!!editDepTarget}
        onClose={() => {
          if (!isSavingDep) setEditDepTarget(null);
        }}
        title={`Edit — ${editDepTarget?.ciName ?? ""}`}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setEditDepTarget(null)}
            onSubmit={handleSaveEditDep}
            submitLabel={isSavingDep ? "Saving…" : "Save"}
            submitDisabled={isSavingDep}
          />
        }
      >
        {editDepTarget && (
          <div className="space-y-4">
            <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
              <p className="font-medium">
                {editDepTarget.ciType === "CLOUD"
                  ? (editDepTarget as any).cloudResourceName ||
                    editDepTarget.awsResourceName ||
                    editDepTarget.awsResourceIdentifier ||
                    editDepTarget.ciName
                  : editDepTarget.ciName}
              </p>
              <p className="text-xs d">
                {editDepTarget.ciType === "CLOUD"
                  ? [
                      (editDepTarget as any).cloudServiceName ||
                        editDepTarget.awsServiceName,
                      editDepTarget.ciCloudProviderName,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : editDepTarget.ciIpAddress || editDepTarget.ciIdentifier}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Port">
                <FormInput
                  type="number"
                  value={editDepForm.port}
                  onChange={(v) => setEditDepForm({ ...editDepForm, port: v })}
                  placeholder="e.g. 3306"
                />
              </FormField>
              <FormField label="Role" required>
                <FormSelect
                  value={editDepForm.role}
                  onChange={(v) =>
                    setEditDepForm({ ...editDepForm, role: v as AssetRole })
                  }
                  options={ROLE_OPTIONS}
                />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Protocol">
                <FormSelect
                  value={editDepForm.protocol}
                  onChange={(v) =>
                    setEditDepForm({
                      ...editDepForm,
                      protocol: v as AssetProtocol,
                    })
                  }
                  options={PROTOCOL_OPTIONS}
                />
              </FormField>
              <FormField label="Status">
                <FormSelect
                  value={editDepForm.status}
                  onChange={(v) =>
                    setEditDepForm({ ...editDepForm, status: v as AssetStatus })
                  }
                  options={DEP_STATUS_OPTIONS}
                />
              </FormField>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Dialogs ────────────────────────────────────────────────────── */}
      <DeleteConfirmDialog
        open={showDelete}
        type="Service"
        name={service.name}
        deleting={isDeleting}
        onCancel={() => setShowDelete(false)}
        onConfirm={handleDelete}
      />
      <DeleteConfirmDialog
        open={!!deletingComp}
        type="Component"
        name={deletingComp?.component.title ?? ""}
        deleting={Object.values(isRemovingComp).some(Boolean)}
        onCancel={() => setDeletingComp(null)}
        onConfirm={confirmRemoveComp}
      />
      <DeleteConfirmDialog
        open={!!deletingDep}
        type="Infrastructure"
        name={deletingDep?.ciName ?? "this infrastructure"}
        deleting={Object.values(isRemovingDep).some(Boolean)}
        onCancel={() => setDeletingDep(null)}
        onConfirm={confirmRemoveDep}
      />
    </div>
  );
}
